import _XIcon from "@adsk/alloy-react-icon/es/lib/XIcon";
import _styled from "styled-components";
const _excluded = ["style", "className", "status", "showIcon", "children", "footer", "dismissible", "onDismiss", "asyncOperation", "autoDismiss"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import styledC, { keyframes } from 'styled-components';
import { BasicButton } from '@adsk/alloy-react-button';
import { variantSwitch, useDeprecate } from '@adsk/alloy-react-helpers';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import { NOTIFICATION_STATUSES } from './helpers/consts';
import NotificationIcon from './NotificationIcon';
const fadeIn = keyframes(["from{top:-16px;opacity:0;}to{top:0;opacity:1;}"]);
const StyledDiv = styledC.div.withConfig({
  displayName: "Notification__StyledDiv",
  componentId: "sc-hugixw-0"
})(["min-width:320px;max-width:416px;min-height:48px;background-color:", ";display:flex;flex-direction:column;border-radius:", "px;border-left:", " solid 4px;box-shadow:", ";padding:", "px ", "px;box-sizing:border-box;animation:", " 0.2s ease-out;"], theme.tokens.colors.background.primary.value, theme.borderRadius.medium, theme.tokens.colors.border.information.value, theme.shadows.lowElevation, theme.spacing.S, theme.spacing.M, fadeIn);
var _StyledStyledDiv = styledC(StyledDiv).withConfig({
  displayName: "Notification___StyledStyledDiv",
  componentId: "sc-hugixw-1"
})(["", ""], p => p.$_css);
const StyledContent = styledC.div.withConfig({
  displayName: "Notification__StyledContent",
  componentId: "sc-hugixw-2"
})(["display:flex;"]);
const StyledMessage = styledC.div.withConfig({
  displayName: "Notification__StyledMessage",
  componentId: "sc-hugixw-3"
})(["flex:1;padding:", "px 0;word-break:break-word;"], theme.spacing.XXXS);
const StyledBasicButton = styledC(BasicButton).withConfig({
  displayName: "Notification__StyledBasicButton",
  componentId: "sc-hugixw-4"
})(["height:24px;color:", ";margin-left:26px;"], theme.tokens.colors.icon.secondary.value);
const StyleNotificationIcon = styledC(NotificationIcon).withConfig({
  displayName: "Notification__StyleNotificationIcon",
  componentId: "sc-hugixw-5"
})(["margin-right:10px;"]);
const Notification = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      style,
      className,
      status = NOTIFICATION_STATUSES.INFO,
      showIcon = true,
      children,
      footer,
      dismissible = true,
      onDismiss,
      asyncOperation,
      autoDismiss
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  useDeprecate(autoDismiss !== undefined, 'Notification', 'autoDismiss', 'useNotifications().showNotification autoDismiss argument');
  const hasPromise = asyncOperation && typeof asyncOperation.then === 'function';
  const [asyncResult, setAsyncResult] = React.useState();
  React.useEffect(() => {
    if (hasPromise) {
      asyncOperation === null || asyncOperation === void 0 ? void 0 : asyncOperation.then(result => setAsyncResult(result)).catch(result => setAsyncResult(result));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isLoading = hasPromise && !asyncResult;
  const finalStatus = hasPromise && asyncResult && asyncResult.status ? asyncResult.status : status;
  const asyncDismissible = hasPromise && asyncResult;
  const finalShowIcon = showIcon && status !== NOTIFICATION_STATUSES.NONE;
  return /*#__PURE__*/React.createElement(_StyledStyledDiv, _extends({}, props, {
    className: className,
    ref: ref,
    role: "alert",
    $_css: [variantSwitch({
      statement: finalStatus,
      variants: {
        [NOTIFICATION_STATUSES.NONE]: {
          borderColor: 'transparent'
        },
        [NOTIFICATION_STATUSES.INFO]: {
          borderColor: theme.tokens.colors.border.information.value,
          backgroundColor: theme.tokens.colors.background.information.subtler.value
        },
        [NOTIFICATION_STATUSES.SUCCESS]: {
          borderColor: theme.tokens.colors.border.success.value,
          backgroundColor: theme.tokens.colors.background.success.subtler.value
        },
        [NOTIFICATION_STATUSES.WARNING]: {
          borderColor: theme.tokens.colors.border.warning.value,
          backgroundColor: theme.tokens.colors.background.warning.subtler.value
        },
        [NOTIFICATION_STATUSES.ERROR]: {
          borderColor: theme.tokens.colors.border.error.value,
          backgroundColor: theme.tokens.colors.background.error.subtler.value
        }
      }
    }), style]
  }), /*#__PURE__*/React.createElement(StyledContent, null, finalShowIcon && !isLoading && /*#__PURE__*/React.createElement(StyleNotificationIcon, {
    status: finalStatus
  }), isLoading && /*#__PURE__*/React.createElement(ProgressRing, {
    style: {
      marginRight: theme.spacing.M
    },
    size: ProgressRing.SIZES.SMALL
  }), /*#__PURE__*/React.createElement(StyledMessage, null, asyncResult && asyncResult.message ? asyncResult.message : children), (dismissible !== false || asyncDismissible) && /*#__PURE__*/React.createElement(StyledBasicButton, {
    "aria-label": "close",
    onClick: onDismiss
  }, /*#__PURE__*/React.createElement(_XIcon, null))), asyncResult && 'footer' in asyncResult ? asyncResult.footer : footer);
});
Notification.displayName = 'Notifications';
Notification.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Notification's content */
  children: PropTypes.any,
  /** Notification's footer */
  footer: PropTypes.any,
  /** Called on dismiss */
  onDismiss: PropTypes.func,
  /** Deprecated: Controls whether the notification should automatically dismiss */
  autoDismiss: PropTypes.number,
  /** Controls whether the notification is dismissible */
  dismissible: PropTypes.bool,
  /** Toggle icon visibility */
  showIcon: PropTypes.bool,
  /** Notification status */
  status: PropTypes.oneOf(Object.values(NOTIFICATION_STATUSES)),
  /** A Promise if the toast needs to be changed after rendered.
   When the promise resolves it should return an object with `message` and `status` to update the toast state. */
  asyncOperation: PropTypes.instanceOf(Promise)
};
export default Notification;
//# sourceMappingURL=Notification.js.map